import { useEffect, useState } from 'react'
import { NUMBER_OF_STEPS } from './constants'
import type {
  NewReadingGoalModalProps,
  SelectedClass
} from './NewReadingGoalModal.types'
import {
  createOrUpdateReadingGoals,
  getReadingGoalsByEntities
} from 'api/mutations/reading-goals'
import { useMutation, useQuery } from '@apollo/client'
import type {
  GetReadingGoalsByEntitiesQueryType,
  ReadingGoal
} from 'api/data.types'
import useToast from 'utils/hooks/use-toast'

export const useNewReadingGoalModal = ({
  onClose,
  entityGoalEditing
}: {
  onClose: () => void
  entityGoalEditing: NewReadingGoalModalProps['entityGoalEditing']
}) => {
  const [currentStep, setCurrentStep] = useState<number>(0)
  const [selectedClasses, setSelectedClasses] = useState<SelectedClass[]>([])
  const [selectedReadingGoalTime, setSelectedReadingGoalTime] =
    useState<number>(0)
  const [isQuitting, setIsQuitting] = useState(false)
  const [successCreatingGoal, setSuccessCreatingGoal] = useState<
    null | boolean
  >(null)
  const [createOrUpdateReadingGoalsMutation] = useMutation(
    createOrUpdateReadingGoals
  )
  const [submitting, setSubmitting] = useState(false)
  const { toastMessage } = useToast()
  const [showEditionSummary, setShowEdittionSummary] = useState(
    Boolean(entityGoalEditing)
  )
  const { data: currentReadingGoals } =
    useQuery<GetReadingGoalsByEntitiesQueryType>(getReadingGoalsByEntities, {
      variables: {
        entityIds: selectedClasses.map(c => c.id)
      }
    })

  useEffect(() => {
    setShowEdittionSummary(Boolean(entityGoalEditing))

    if (entityGoalEditing) {
      setCurrentStep(0)
      setSelectedClasses([
        {
          id: entityGoalEditing.entityId,
          label: entityGoalEditing.entityName
        }
      ])
      setSelectedReadingGoalTime(entityGoalEditing.goal)
    }
  }, [entityGoalEditing])

  const _onClose = () => {
    if (!isQuitting && !successCreatingGoal) {
      setIsQuitting(true)
      return
    }

    resetStep()
    onClose()
    setIsQuitting(false)
    setSuccessCreatingGoal(null)
  }

  const handlePreviousStep = () => {
    if (currentStep === 0) {
      return _onClose()
    }

    setCurrentStep(step => step - 1)
  }

  const handleClickEditGoal = () => {
    setShowEdittionSummary(false)
    setCurrentStep(1)
  }

  const handleNextStep = () => {
    if (currentStep === NUMBER_OF_STEPS - 1) {
      return _onClose()
    }
    setCurrentStep(step => step + 1)
  }

  const resetStep = () => {
    setCurrentStep(0)
    setSelectedReadingGoalTime(0)
    setSelectedClasses([])
  }

  const handleClassSelection = ({
    item,
    updatedCheck
  }: {
    item: SelectedClass
    updatedCheck: boolean
  }) => {
    if (!updatedCheck) {
      const filteredSelectedClasses = selectedClasses.filter(element => {
        return element.id !== item.id
      })
      return setSelectedClasses(filteredSelectedClasses)
    }
    setSelectedClasses([...selectedClasses, item])
  }

  const handleSubmit = () => {
    setSubmitting(true)
    createOrUpdateReadingGoalsMutation({
      variables: {
        goal: selectedReadingGoalTime,
        entityIds: selectedClasses.map(c => c.id)
      }
    })
      .then(() => {
        setSubmitting(false)
        setSuccessCreatingGoal(true)
        setCurrentStep(0)
      })
      .catch(err => {
        toastMessage({
          message:
            'Houve um problema em criar a Meta de Leitura. Tente novamente.',
          type: 'error'
        })
        setSubmitting(false)
      })
  }

  return {
    currentStep,
    handlePreviousStep,
    handleNextStep,
    _onClose,
    handleClassSelection,
    selectedClasses,
    selectedReadingGoalTime,
    setSelectedReadingGoalTime,
    isQuitting,
    setIsQuitting,
    successCreatingGoal,
    setSuccessCreatingGoal,
    currentReadingGoals:
      currentReadingGoals?.readingGoals || ([] as ReadingGoal[]),
    handleSubmit,
    submitting,
    handleClickEditGoal,
    showEditionSummary,
    setShowEdittionSummary,
    setCurrentStep
  }
}
